/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef, useEffect } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Collapse from '@material-ui/core/Collapse';
import PulseLoader from 'react-spinners/PulseLoader';

// Material UI
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import NoSsr from '@material-ui/core/NoSsr';
import Typography from '@material-ui/core/Typography';

// app: actions
import { pollUser } from 'actions/user/refreshUser';
import { purchase, restorePurchases } from 'actions/inAppPayment';
import { applyProduct } from 'actions/paywall';

// app: components
import Heading from 'components/Heading';
import MembershipBenefits from 'components/MembershipBenefits';
import PaddleCheckout from 'components/PaddleCheckout';
import SubscriptionPlanChooser from 'components/SubscriptionPlanChooser';

// app modules: services
import history from 'services/history';
import { isApp } from 'services/environment';

// app: constants
import { COLOURS } from 'constants/index';

// assets
import CssUpgradeToPremium from './UpgradeToPremium.scss';

function UpgradeToPremium(props) {
  const {
    annualPlanId,
    buttonText,
    buttonTextWithTrial,
    buttonTextWithCost,
    checkoutCompleteHasHeading,
    checkoutCompleteMessage,
    checkoutRoute,
    children,
    className,
    completionContent,
    defaultCoupon,
    extraInfo,
    extraPassthrough,
    headingClassName,
    hideBenefits,
    monthlyPlanId,
    onCheckoutComplete,
    onCheckoutOpen,
    onExit,
    overridePlanId,
    planChooserProps,
    secondaryAction,
    title,
    withInlinePlanSelector,
    withInlineCheckout,
    withBenefits,
  } = props;
  useStyles(CssUpgradeToPremium);
  const isDev = useSelector(state => state.config.environment === 'DEV');
  const theme = useSelector(state => state.config.paywall.theme || props.theme);
  const requestedCoupon = useSelector(state => state.paywall.requestedCoupon || defaultCoupon);
  const existingSubscriptionStatus = useSelector(
    state => (state.user.subscription ? state.user.subscription.status : null),
  );
  const subscriptionIsNew = useSelector(
    state => (state.user.subscription ? state.user.subscription.is_new : false),
  );
  const dispatch = useDispatch();
  const planIdFromLocationState = useSelector(
    state => state.router.locationState?.planId || state.router.queryParams?.pr,
  );
  const initiallySelectedPlanId = planIdFromLocationState || overridePlanId;
  const hideBenefitsFromLocationState = useSelector(
    state => state.router.state !== 'loading' && state.router.locationState?.hideBenefits,
  );
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [planChosen, setPlanChosen] = useState(false);
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [defaultButtonText, setDefaultButtonText] = useState(buttonTextWithCost);
  const scrollTargetRef = useRef(null);

  useEffect(
    () => {
      const availablePlans = [annualPlanId, monthlyPlanId, overridePlanId];
      if (
        !planChosen &&
        !availablePlans.includes(selectedPlanId) &&
        availablePlans.includes(initiallySelectedPlanId)
      ) {
        setSelectedPlanId(initiallySelectedPlanId);
      }
    },
    [
      annualPlanId,
      monthlyPlanId,
      overridePlanId,
      planChosen,
      initiallySelectedPlanId,
      selectedPlanId,
    ],
  );

  const handleSelectPlan = (planId, planInfo) => {
    if (isApp()) {
      // For the in app purchase trigger the native payment. Does nothing for web.
      // For now just pick the first discount.
      const discount =
        planInfo && planInfo.discounts && planInfo.discounts.length ? planInfo.discounts[0] : null;
      console.info('planId, discount, planInfo = ', planId, planInfo, discount);
      dispatch(purchase(planId, discount));
    } else if (withInlineCheckout && planId) {
      setSelectedPlanId(planId);
      setPlanChosen(true);
    } else {
      // Send to the full page subscribe flow. Best for mobile devices.
      if (withInlinePlanSelector && planId) {
        dispatch(applyProduct(String(planId)));
      }
      if (checkoutRoute) {
        history.push(checkoutRoute);
      } else {
        history.push('/subscribe', {
          planId: withInlinePlanSelector ? planId : null,
          returnTo: props.returnTo || history.location,
          hideBenefits: true,
        });
      }
    }
  };

  const handleChangePlan = (planId, plan, hasDoneGGTrial, platformId, isAnnual = false) => {
    setSelectedPlanId(planId);

    // eslint-disable-next-line camelcase
    let isTrial = plan?.trial_days > 0;

    if (platformId === 'android' && !isAnnual) {
      isTrial = !hasDoneGGTrial;
    } else if (isAnnual) {
      isTrial = false;
    }

    setDefaultButtonText(isTrial ? buttonTextWithTrial : buttonTextWithCost);
  };

  const scrollToView = () => {
    if (scrollTargetRef.current) {
      window.scrollTo({ left: 0, top: scrollTargetRef.current.offsetTop - 60, behavior: 'smooth' });
    }
  };

  const handleCheckoutLoadComplete = () => {
    setCheckoutLoaded(true);
    if (typeof onCheckoutOpen === 'function') {
      onCheckoutOpen();
    }
    scrollToView();
  };

  const handleCheckoutSuccess = () => {
    setTimeout(scrollToView, 100);
    dispatch(
      pollUser({
        provider: 'paddle',
        is_new: true,
        status: existingSubscriptionStatus || 'pending',
        is_valid: false,
      }),
    );
    setCheckoutComplete(true);
    if (typeof onCheckoutComplete === 'function') {
      onCheckoutComplete();
    }
  };

  if (!checkoutComplete && !subscriptionIsNew) {
    const checkout =
      withInlineCheckout &&
      selectedPlanId &&
      (planChosen || planIdFromLocationState) &&
      !isApp() ? (
        <Collapse className="upgrade-to-premium__full-width" in={checkoutLoaded} timeout={300}>
          <PaddleCheckout
            productId={parseInt(selectedPlanId, 10)}
            coupon={requestedCoupon}
            onCheckoutSuccess={handleCheckoutSuccess}
            loadCallback={handleCheckoutLoadComplete}
            extraPassthrough={extraPassthrough}
          />
          <NoSsr>
            {isDev || window.Cypress ? (
              <button onClick={handleCheckoutSuccess}>(DEV): Simulate completion</button>
            ) : null}
          </NoSsr>
        </Collapse>
      ) : null;

    let extraInfoContent;
    if (extraInfo) {
      extraInfoContent =
        typeof extraInfo === 'string' ? (
          <p className="margin-bottom-10">{extraInfo}</p>
        ) : (
          <div className="upgrade-to-premium__full-width margin-bottom-10">{extraInfo}</div>
        );
    }
    return (
      <div className={`upgrade-to-premium ${className}`}>
        {title ? (
          <Heading level={2} className={headingClassName}>
            {title}
          </Heading>
        ) : null}
        {extraInfoContent}
        {hideBenefitsFromLocationState || !withBenefits ? null : (
          <Collapse in={hideBenefits === false} timeout={300}>
            <MembershipBenefits premium itemClassName="margin-bottom-10" />
          </Collapse>
        )}
        {children}
        <div ref={scrollTargetRef} />
        {withInlinePlanSelector ? (
          <SubscriptionPlanChooser
            annualPlanId={annualPlanId}
            monthlyPlanId={monthlyPlanId}
            overridePlanId={overridePlanId}
            requestedCoupon={requestedCoupon}
            selectedPlanId={selectedPlanId}
            onChange={handleChangePlan}
            onSubmit={handleSelectPlan}
            inProgress={planChosen && !!selectedPlanId}
            hideButton={checkoutLoaded || (!!planIdFromLocationState && withInlineCheckout)}
            buttonText={buttonText || defaultButtonText}
            heading={initiallySelectedPlanId ? null : 'Select a plan'}
            theme={theme}
            showPromoTermInfo={checkoutLoaded}
            {...planChooserProps}
          />
        ) : (
          <Button
            className="margin-left-20 margin-right-20"
            variant="contained"
            size="large"
            color="primary"
            onClick={() => handleSelectPlan(selectedPlanId)}
          >
            {buttonText}
          </Button>
        )}
        {secondaryAction}
        {withInlineCheckout && planIdFromLocationState && !checkoutLoaded ? (
          <PulseLoader loading color={COLOURS.ENERGIZED} sizeUnit="px" size={8} />
        ) : null}
        {checkout}
        {isApp() ? (
          <Typography align="center" className="margin-top-10">
            <Link color="primary" onClick={() => dispatch(restorePurchases())}>
              Restore purchases
            </Link>{' '}
            &middot;{' '}
            <Link color="primary" href="https://www.ckbk.com/terms/">
              Terms of service
            </Link>
          </Typography>
        ) : null}
      </div>
    );
  }
  // Default completion content...
  return (
    <div className={`upgrade-to-premium ${className}`}>
      <div ref={scrollTargetRef} />
      {completionContent || (
        <React.Fragment>
          {checkoutCompleteHasHeading ? (
            <Heading level={2} className={headingClassName}>
              {checkoutCompleteMessage}
            </Heading>
          ) : (
            <p className="margin-bottom-20">{checkoutCompleteMessage}</p>
          )}
          <p className="margin-bottom-20">We will email you a receipt shortly</p>
          <Button variant="contained" size="large" color="primary" onClick={onExit}>
            Back to the Recipes
          </Button>
        </React.Fragment>
      )}
    </div>
  );
}

UpgradeToPremium.propTypes = {
  annualPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  buttonText: PropTypes.string,
  buttonTextWithTrial: PropTypes.string,
  buttonTextWithCost: PropTypes.string,
  checkoutCompleteHasHeading: PropTypes.bool,
  checkoutCompleteMessage: PropTypes.string,
  checkoutRoute: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  className: PropTypes.string,
  children: PropTypes.node,
  completionContent: PropTypes.node,
  defaultCoupon: PropTypes.string,
  extraInfo: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  extraPassthrough: PropTypes.object,
  headingClassName: PropTypes.string,
  hideBenefits: PropTypes.bool,
  monthlyPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onCheckoutOpen: PropTypes.func,
  onCheckoutComplete: PropTypes.func,
  onExit: PropTypes.func,
  overridePlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // eslint-disable-next-line react/forbid-prop-types
  planChooserProps: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  returnTo: PropTypes.object,
  secondaryAction: PropTypes.node,
  theme: PropTypes.oneOf(['light', 'dark', 'system']),
  title: PropTypes.node,
  withBenefits: PropTypes.bool,
  withInlineCheckout: PropTypes.bool,
  withInlinePlanSelector: PropTypes.bool,
};

UpgradeToPremium.defaultProps = {
  annualPlanId: null,
  buttonText: null,
  buttonTextWithTrial: 'Start Free Trial',
  buttonTextWithCost: 'Start Subscription',
  checkoutCompleteHasHeading: true,
  checkoutCompleteMessage: 'Welcome to ckbk Premium Membership',
  checkoutRoute: null,
  children: null,
  className: '',
  completionContent: null,
  defaultCoupon: null,
  extraInfo: null,
  extraPassthrough: {},
  headingClassName: '',
  hideBenefits: false,
  monthlyPlanId: null,
  onCheckoutOpen: null,
  onCheckoutComplete: null,
  onExit: () => {
    history.push('/');
  },
  overridePlanId: null,
  planChooserProps: {},
  returnTo: null,
  secondaryAction: null,
  theme: null,
  title: (
    <React.Fragment>
      Upgrade to ckbk <span style={{ whiteSpace: 'nowrap' }}>Premium Membership</span>
    </React.Fragment>
  ),
  withBenefits: false,
  withInlineCheckout: true,
  withInlinePlanSelector: true,
};

export default UpgradeToPremium;
